import { useState, type ReactElement, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { apiClient } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { AdminNav } from "./AdminPage";

/**
 * ЦИКЛ B / B1 — карточка лида (детальный просмотр + действия).
 *
 * Backend (Cycle 11) live: GET /api/admin/leads/{id}, PATCH /api/admin/leads/{id}.
 * B2 (live): POST /api/admin/leads/{id}/invite — конвертация в персональную ссылку.
 * Calls (Cycle 17): GET /api/admin/calls?lead_id={id} — история звонков по лиду.
 */

const STATUSES = ["new", "contacted", "qualified", "closed_won", "closed_lost"] as const;
type LeadStatus = (typeof STATUSES)[number];

const STATUS_LABELS: Record<LeadStatus, { ru: string; color: string; bg: string }> = {
  new: { ru: "Новый", color: "#0b5fff", bg: "#e6f0ff" },
  contacted: { ru: "Контакт", color: "#a07000", bg: "#fff7d6" },
  qualified: { ru: "Квалифицирован", color: "#0a7a4a", bg: "#e6f7ee" },
  closed_won: { ru: "Закрыт ✓", color: "#0a7a4a", bg: "#d6f5e3" },
  closed_lost: { ru: "Закрыт ✗", color: "#a01c2a", bg: "#fde8eb" },
};

type Lead = {
  id: string;
  name: string;
  email: string;
  company: string;
  role: string | null;
  phone: string | null;
  products_of_interest: string[];
  message: string | null;
  source_ip: string | null;
  status: string;
  created_at: string;
};

type Call = {
  id: string;
  phone: string;
  status: string;
  notes: string | null;
  requested_at?: string;
};

// ЦИКЛ C / C2 — вовлечённость проспекта по выданным ссылкам.
type ProductView = {
  product_slug: string;
  launch_count: number;
  first_at: string;
  last_at: string;
  total_duration_sec: number | null;
};
type InviteActivity = {
  token_preview: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  is_active: boolean;
  catalog_subset: string[] | null;
  sessions_count: number;
  launches_count: number;
  matched_by: "lead_id" | "email";
};
type Engagement = {
  lead_id: string;
  invites_count: number;
  opened: boolean;
  first_activity: string | null;
  last_activity: string | null;
  products_viewed: ProductView[];
  invites: InviteActivity[];
};

function fmtDur(sec: number | null): string {
  if (sec == null || sec <= 0) return "";
  if (sec < 60) return `${sec} с`;
  const m = Math.floor(sec / 60);
  return m < 60 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60} мин`;
}

function fmt(iso: string | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ru", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 12, padding: "10px 0", borderTop: "1px solid #eef1f7", fontSize: 14 }}>
      <div style={{ color: "#8693b0" }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

export function AdminLeadDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const { hasAdminJwt } = useAuth();
  const qc = useQueryClient();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: lead, isLoading, error } = useQuery<Lead>({
    queryKey: ["admin-lead", id],
    queryFn: async () => (await apiClient.get<Lead>(`/api/admin/leads/${id}`)).data,
    enabled: hasAdminJwt && !!id,
  });

  const { data: calls } = useQuery<{ items: Call[] }>({
    queryKey: ["admin-lead-calls", id],
    queryFn: async () => (await apiClient.get<{ items: Call[] }>(`/api/admin/calls`, { params: { lead_id: id } })).data,
    enabled: hasAdminJwt && !!id,
  });

  // C2/C3: вовлечённость проспекта по выданным ссылкам.
  const { data: engagement } = useQuery<Engagement>({
    queryKey: ["admin-lead-engagement", id],
    queryFn: async () => (await apiClient.get<Engagement>(`/api/admin/leads/${id}/engagement`)).data,
    enabled: hasAdminJwt && !!id,
  });

  const updateStatus = useMutation({
    mutationFn: async (status: LeadStatus) => (await apiClient.patch(`/api/admin/leads/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-lead", id] }),
  });

  const createInvite = useMutation({
    mutationFn: async () => (await apiClient.post<{ url: string }>(`/api/admin/leads/${id}/invite`)).data,
    onSuccess: (data) => {
      setCopied(false);
      setInviteUrl(data.url);
    },
  });

  // B3: быстрый запрос звонка по лиду (создаёт Call → история ниже)
  const createCall = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/admin/calls`, {
        lead_id: id,
        phone: lead?.phone,
        contact_name: lead?.name,
        notes: "Запрос звонка из карточки лида",
      })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-lead-calls", id] }),
  });

  if (!hasAdminJwt) {
    return (
      <section style={{ padding: 32, maxWidth: 600, margin: "0 auto" }}>
        <h1>Карточка лида</h1>
        <p style={{ color: "#5a6378" }}>Требуется admin authentication. <Link to="/admin">Войти как admin</Link></p>
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 860, margin: "0 auto" }}>
      <AdminNav />
      <div style={{ padding: "0 24px" }}>
        <Link to="/admin/leads" style={{ color: "#0b5fff", fontSize: 13 }}>← К списку лидов</Link>

        {isLoading && <p>Загрузка…</p>}
        {error && <p style={{ color: "#a01c2a" }}>Лид не найден или ошибка загрузки.</p>}

        {lead && (
          <>
            <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "12px 0 8px", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h1 style={{ margin: "0 0 2px" }}>{lead.name}</h1>
                <p style={{ margin: 0, color: "#5a6378", fontSize: 14 }}>{lead.company}{lead.role ? ` · ${lead.role}` : ""}</p>
              </div>
              <button
                onClick={() => createInvite.mutate()}
                disabled={createInvite.isPending}
                style={{ padding: "10px 16px", background: "#0b5fff", color: "white", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}
              >
                🔗 Выдать ссылку каталога
              </button>
            </header>

            {inviteUrl && (
              <div style={{ margin: "0 0 16px", padding: 14, background: "#e6f7ee", border: "1px solid #0a7a4a", borderRadius: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input readOnly value={inviteUrl} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, minWidth: 260, padding: "8px 12px", border: "1px solid #b8d8c6", borderRadius: 6, fontSize: 13, fontFamily: "monospace" }} />
                  <button
                    onClick={async () => { try { await navigator.clipboard.writeText(inviteUrl); setCopied(true); } catch { /* manual copy */ } }}
                    style={{ padding: "8px 14px", background: copied ? "#0a7a4a" : "#0b5fff", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                  >
                    {copied ? "✓ Скопировано" : "Копировать"}
                  </button>
                </div>
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#5a6378" }}>Отправьте ссылку клиенту — он увидит подобранный набор продуктов.</p>
              </div>
            )}

            <div style={{ background: "white", border: "1px solid #e1e6f0", borderRadius: 10, padding: "8px 20px 16px" }}>
              <Row label="Статус">
                <select
                  value={lead.status}
                  onChange={(e) => updateStatus.mutate(e.target.value as LeadStatus)}
                  style={{ padding: "5px 10px", border: "1px solid", borderColor: STATUS_LABELS[lead.status as LeadStatus]?.color ?? "#d1d5e0", borderRadius: 5, fontSize: 13, background: STATUS_LABELS[lead.status as LeadStatus]?.bg ?? "white", color: STATUS_LABELS[lead.status as LeadStatus]?.color ?? "#0a1340", fontWeight: 600, cursor: "pointer" }}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s].ru}</option>)}
                </select>
              </Row>
              <Row label="Email"><a href={`mailto:${lead.email}`} style={{ color: "#0b5fff" }}>{lead.email}</a></Row>
              <Row label="Телефон">{lead.phone ? <a href={`tel:${lead.phone}`} style={{ color: "#0b5fff" }}>{lead.phone}</a> : "—"}</Row>
              <Row label="Продукты интереса">{lead.products_of_interest.length ? lead.products_of_interest.join(", ") : "—"}</Row>
              <Row label="Сообщение">{lead.message || "—"}</Row>
              <Row label="Создан">{fmt(lead.created_at)}</Row>
              <Row label="Источник (IP)">{lead.source_ip || "—"}</Row>
            </div>

            {/* C2/C3 — Активность проспекта по выданным ссылкам */}
            <h2 style={{ fontSize: 16, margin: "24px 0 8px" }}>📊 Активность проспекта</h2>
            {engagement && engagement.invites_count > 0 ? (
              <div style={{ background: "white", border: "1px solid #e1e6f0", borderRadius: 10, padding: "16px 20px" }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600, background: engagement.opened ? "#e6f7ee" : "#f0f2f7", color: engagement.opened ? "#0a7a4a" : "#8693b0" }}>
                    {engagement.opened ? "● Открыл ссылку" : "○ Ещё не открывал"}
                  </span>
                  <span style={{ fontSize: 13, color: "#5a6378" }}>Ссылок выдано: <strong>{engagement.invites_count}</strong></span>
                  {engagement.last_activity && (
                    <span style={{ fontSize: 13, color: "#5a6378" }}>Последняя активность: <strong>{fmt(engagement.last_activity)}</strong></span>
                  )}
                </div>

                {engagement.products_viewed.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: "#8693b0", marginBottom: 4 }}>Запускал продукты:</div>
                    {engagement.products_viewed.map((p) => (
                      <div key={p.product_slug} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: "1px solid #eef1f7", fontSize: 13 }}>
                        <span><strong>{p.product_slug}</strong> · запусков: {p.launch_count}{p.total_duration_sec ? ` · ${fmtDur(p.total_duration_sec)}` : ""}</span>
                        <span style={{ color: "#8693b0", whiteSpace: "nowrap" }}>{fmt(p.last_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {engagement.opened && engagement.products_viewed.length === 0 && (
                  <p style={{ margin: "12px 0 0", fontSize: 13, color: "#8693b0" }}>Открыл портал, но демо продуктов пока не запускал.</p>
                )}
              </div>
            ) : (
              <p style={{ color: "#8693b0", fontSize: 13 }}>
                {engagement ? "Лиду ещё не выдавалась ссылка — нажмите «🔗 Выдать ссылку каталога»." : "Загрузка активности…"}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "24px 0 8px" }}>
              <h2 style={{ fontSize: 16, margin: 0 }}>История звонков {calls?.items?.length ? `(${calls.items.length})` : ""}</h2>
              <button
                onClick={() => createCall.mutate()}
                disabled={!lead.phone || createCall.isPending}
                title={lead.phone ? "Создать запрос звонка по этому лиду" : "У лида нет телефона"}
                style={{ padding: "6px 12px", background: "#fff", color: lead.phone ? "#0a7a4a" : "#aaa", border: `1px solid ${lead.phone ? "#0a7a4a" : "#ccc"}`, borderRadius: 6, cursor: lead.phone ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600 }}
              >
                {createCall.isPending ? "…" : createCall.isSuccess ? "✓ Запрошен" : "📞 Запросить звонок"}
              </button>
            </div>
            {calls && calls.items.length > 0 ? (
              <div style={{ background: "white", border: "1px solid #e1e6f0", borderRadius: 10, padding: "8px 20px 12px" }}>
                {calls.items.map((c) => (
                  <Row key={c.id} label={fmt(c.requested_at)}>
                    <span>📞 {c.phone} · <strong>{c.status}</strong>{c.notes ? ` · ${c.notes}` : ""}</span>
                  </Row>
                ))}
              </div>
            ) : (
              <p style={{ color: "#8693b0", fontSize: 13 }}>Звонков по лиду пока нет.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
